/**
 * De afsluitknop.
 *
 * Afsluiten mag pas als elk toestel klaar is met syncen. Het dashboard kan dat
 * niet zelf zien — de wachtrij staat op het toestel — dus meldt elke kassa hoe
 * hij ervoor staat. Geen bericht is ook een bericht: een toestel dat een half
 * uur stil is heeft misschien twintig bonnen die nog moeten komen.
 *
 * De melding is altijd in gewone taal. "Bar de Dorst heeft nog 3 bonnen die
 * niet verstuurd zijn", niet "sync pending".
 */

export interface Kassastand {
  kraamdeelnameId: string;
  kraamnaam: string;
  wachtendeBonnen: number;
  /** Wanneer deze kassa voor het laatst van zich liet horen. */
  laatstGezien: string | null;
  /** Een kraam die nooit is begonnen hoeft niets te melden. */
  isActief: boolean;
  /** Regels die de server blijft weigeren. Die komen nooit vanzelf goed. */
  vastgelopen?: number;
  /** Draait een kraam met twee toestellen, dan staat hier welk toestel. */
  toestelnaam?: string;
}

export interface Blokkade {
  kraamdeelnameId: string;
  kraamnaam: string;
  reden: string;
}

export interface Afsluitcontrole {
  magAfsluiten: boolean;
  blokkades: Blokkade[];
}

export const STILTE_GRENS_MINUTEN = 5;

function minutenTussen(vroeger: string, later: Date): number {
  return Math.floor((later.getTime() - new Date(vroeger).getTime()) / 60_000);
}

/**
 * Kijkt of het evenement dicht mag. Weigert zolang er ergens bonnen wachten of
 * een actieve kassa te lang stil is.
 */
export function controleerAfsluiten(
  standen: readonly Kassastand[],
  nu: Date,
  stilteGrensMinuten = STILTE_GRENS_MINUTEN,
): Afsluitcontrole {
  const blokkades: Blokkade[] = [];

  for (const stand of standen) {
    if (!stand.isActief) continue;

    if (stand.wachtendeBonnen > 0) {
      blokkades.push({
        kraamdeelnameId: stand.kraamdeelnameId,
        kraamnaam: stand.kraamnaam,
        reden:
          stand.wachtendeBonnen === 1
            ? 'heeft nog 1 bon die niet verstuurd is'
            : `heeft nog ${stand.wachtendeBonnen} bonnen die niet verstuurd zijn`,
      });
      continue;
    }

    if (stand.laatstGezien === null) {
      blokkades.push({
        kraamdeelnameId: stand.kraamdeelnameId,
        kraamnaam: stand.kraamnaam,
        reden: 'heeft zich nog niet gemeld',
      });
      continue;
    }

    const stil = minutenTussen(stand.laatstGezien, nu);
    if (stil >= stilteGrensMinuten) {
      blokkades.push({
        kraamdeelnameId: stand.kraamdeelnameId,
        kraamnaam: stand.kraamnaam,
        reden: `is al ${stil} minuten stil — mogelijk geen bereik`,
      });
    }
  }

  return { magAfsluiten: blokkades.length === 0, blokkades };
}

/** Wat er op de knop staat als hij weigert. */
export function afsluitMelding(controle: Afsluitcontrole): string {
  if (controle.magAfsluiten) return 'Alle kassa’s zijn bij. Je kunt afsluiten.';
  const eerste = controle.blokkades[0];
  if (eerste === undefined) return '';
  if (controle.blokkades.length === 1) {
    return `${eerste.kraamnaam} ${eerste.reden}. Wacht daar even op.`;
  }
  return `${controle.blokkades.length} kramen zijn nog niet klaar. Wacht daar even op.`;
}

export type Waarschuwingsoort = 'stil' | 'wachtrij' | 'vastgelopen';

export interface Waarschuwing {
  soort: Waarschuwingsoort;
  kraamdeelnameId: string;
  kraamnaam: string;
  /** In gewone taal, klaar om op het dashboard te zetten. */
  melding: string;
  /** Vraagt dit om ingrijpen tijdens het evenement? */
  dringend: boolean;
}

/**
 * Waar de organisator tijdens het evenement iets van moet weten.
 *
 * Alleen wat je nú kunt oplossen. Een kassa die twee minuten stil is hoeft niet
 * op het scherm; een kassa die een half uur stil is wel, want dan staat er
 * misschien iemand met een toestel zonder bereik en loopt de omzet mis.
 */
export function maakWaarschuwingen(
  standen: readonly Kassastand[],
  nu: Date,
  stilteGrensMinuten = 15,
): Waarschuwing[] {
  const waarschuwingen: Waarschuwing[] = [];

  for (const stand of standen) {
    if (!stand.isActief) continue;
    const naam =
      stand.toestelnaam == null ? stand.kraamnaam : `${stand.kraamnaam} (${stand.toestelnaam})`;

    if ((stand.vastgelopen ?? 0) > 0) {
      waarschuwingen.push({
        soort: 'vastgelopen',
        kraamdeelnameId: stand.kraamdeelnameId,
        kraamnaam: naam,
        melding:
          (stand.vastgelopen ?? 0) === 1
            ? `${naam} heeft een bon die niet verstuurd kan worden. Die gaat niet vanzelf weg.`
            : `${naam} heeft ${stand.vastgelopen} bonnen die niet verstuurd kunnen worden. Die gaan niet vanzelf weg.`,
        dringend: true,
      });
    }

    if (stand.laatstGezien === null) continue;

    const stil = Math.floor((nu.getTime() - new Date(stand.laatstGezien).getTime()) / 60_000);
    if (stil >= stilteGrensMinuten) {
      waarschuwingen.push({
        soort: 'stil',
        kraamdeelnameId: stand.kraamdeelnameId,
        kraamnaam: naam,
        melding: `${naam} is al ${stil} minuten stil. Loop er even langs of bel de kraam.`,
        dringend: stil >= stilteGrensMinuten * 2,
      });
    } else if (stand.wachtendeBonnen >= 20) {
      waarschuwingen.push({
        soort: 'wachtrij',
        kraamdeelnameId: stand.kraamdeelnameId,
        kraamnaam: naam,
        melding: `${naam} heeft ${stand.wachtendeBonnen} bonnen die nog niet binnen zijn. Waarschijnlijk slecht bereik.`,
        dringend: false,
      });
    }
  }

  return waarschuwingen;
}
